import React from 'react';
import { observer } from 'mobx-react';
import Spreadsheet from '@tencent/slide-spreadsheet';
import '@tencent/slide-spreadsheet/dist/spreadsheet.css';
import i18n from '@i18next/i18n';
import { Modal as AntModal } from 'antd';
import { Rnd } from 'react-rnd';
import './index-global.less';
import type ChartManager from '@src/slide/application/service/chart-manager';

export interface ChartEditWrapperProps {
    chartManager: ChartManager;
}


@observer
export default class ChartEditWrapper extends React.Component<ChartEditWrapperProps> {
    public state = {
        isEditing: false,
    };

    public changeEditStatus = () => {
        this.setState({
            isEditing: !this.state.isEditing,
        });
    };

    public render() {
        const { chartManager } = this.props;

        return (
            chartManager.isShowChartEditModal && <ChartEditModal chartManager={chartManager}/>
        );
    }
}
const titleHeight = 55;
const defaultSheetWidth = 530;
const defaultSheetHeight = 280;

class ChartEditModal extends React.Component<ChartEditWrapperProps> {
    public state = {
        modalMarginLeft: 40,
        modalMarginBottom: 40,
        sheetWidth: defaultSheetWidth,
        sheetHeight: defaultSheetHeight,
    };
    private chartEditorRef;
    private upX;
    private upY;
    private resized;
    private dragged;
    private rnd;


    public componentDidMount() {
        document.addEventListener('mousedown', this.handleMouseDown);
        document.addEventListener('mouseup', this.handleMouseUp);
        document.addEventListener('click', this.bodyClickHandler);
        window.addEventListener('resize', this.handleResize);
    }

    public componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleMouseDown);
        document.removeEventListener('mouseup', this.handleMouseUp);
        document.removeEventListener('click', this.bodyClickHandler);
        window.removeEventListener('resize', this.handleResize);
    }

    public handleResize = () => {
        this.rnd.updatePosition(this.getModalPosition());
    };

    public getModalPosition() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const x = this.state.modalMarginLeft;
        const y = viewportHeight - (titleHeight + this.state.sheetHeight) - this.state.modalMarginBottom;
        return {
            x,
            y,
        };
    }

    public updateModalBox(x, y, width, height) {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const modalMarginLeft = x;
        const modalMarginBottom = viewportHeight - (titleHeight + height) - y;
        this.setState({
            modalMarginLeft,
            modalMarginBottom,
            sheetWidth: width,
            sheetHeight: height,
        });
    }

    public handleCloseModal = (): void => {
        this.props.chartManager.clearChartModel();
    };

    public handleMouseDown = (e) => {
        this.resized = false;
        this.dragged = false;
        // 按下的时候也更新一下， slide左侧列表iframe派发了mousedown, 没有派发mousedup事件
        this.upX = e.clientX;
        this.upY = e.clientY;
    };

    public handleMouseUp = (e) => {
        this.upX = e.clientX;
        this.upY = e.clientY;
    };

    public bodyClickHandler = (e) => {
        setTimeout(() => {
            if (!this.props.chartManager.isShowChartEditModal || this.resized || this.dragged) return;
            const clickOnContent = this.isClickOnContent(this.upX, this.upY);
            if (!clickOnContent) {
                this.handleCloseModal();
            }
        }, 0);
    };

    public isClickOnContent = (x, y) => {
        if (this.chartEditorRef) {
            const rect = this.chartEditorRef.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right
                && y >= rect.top && y <= rect.bottom) {
                return true;
            }
        }
        return false;
    };

    public render() {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const titleHeight = 55;
        const { chartManager } = this.props;
        const { x, y } = this.getModalPosition();
        return <AntModal
            wrapClassName="slide-spread-sheet-modal"
            title={i18n.t('图表 - 编辑数据')}
            visible={true}
            modalRender={modal => chartManager.isShowChartEditModal && (
                <Rnd
                    ref={c => this.rnd = c}
                    default={{
                        x,
                        y,
                        width: 'auto',
                        height: 'auto',
                    }}
                    className="slide-spread-sheet-dragger"
                    bounds="body"
                    minWidth={316}
                    minHeight={216}
                    onResizeStop={(e, direction, ref, delta, position) => {
                        this.resized = true;
                        const { spreadsheetInstance } = chartManager;
                        const { width, height } = ref.getBoundingClientRect();

                        if (spreadsheetInstance) {
                            spreadsheetInstance.datas[0].settings.view = {
                                width: () => width,
                                height: () => height - titleHeight,
                            };
                            spreadsheetInstance.sheet.sheetReset();
                        }

                        this.updateModalBox(position.x, position.y, width, height);
                    }}
                    onDragStop={(e, data) => {
                        this.dragged = true;
                        this.updateModalBox(data.x, data.y, this.state.sheetWidth, this.state.sheetHeight);
                    }}
                >
                    <div
                        id="chart-editor"
                        ref={(ref) => {
                            this.chartEditorRef = ref;
                        }}
                        style={{
                            width: '100%',
                            height: '100%',
                            background: 'hsla(0, 0%, 100%, .92)',
                            WebkitBackdropFilter: 'blur(16px)',
                            backdropFilter: 'blur(16px)',
                            borderRadius: 4,
                            boxShadow: '0 2px 12px 2px rgb(68 73 77 / 16%)',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        {modal}
                    </div>
                </Rnd>
            )}
            destroyOnClose
            mask={false}
            maskClosable={false}
            footer={null}
            onCancel={this.handleCloseModal}
            style={{
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 9999,
            }}
            closeIcon={
                <div
                    className='modal-close-button'
                    role="button"
                />
            }
        >
            <ChartEditModalContent
                chartManager={this.props.chartManager}
            />
        </AntModal>;
    }
}

export interface ChartEditModalContentlProps {
    chartManager: ChartManager;
}

@observer
export class ChartEditModalContent extends React.Component<ChartEditModalContentlProps> {
    public spreadsheetRef;

    public handlePreventPropagation = (e): void => {
        e?.stopPropagation();
    };

    public handlePreventDefaultAndPropagation = (e): void => {
        e?.preventDefault();
        e?.stopPropagation();
    };

    public handlePreventTwoFingersSwipe = (): void => {
        document.documentElement.style.overscrollBehaviorX = 'none';
        document.body.style.overscrollBehaviorX = 'none';
    };

    public handleOpenTwoFingersSwipe = (): void => {
        document.documentElement.style.overscrollBehaviorX = 'auto';
        document.body.style.overscrollBehaviorX = 'auto';
    };

    public componentDidMount(): void {
        const { chartManager } = this.props;
        const spreadsheet = new Spreadsheet('#slide-chart-spreadsheet', {
            showToolbar: false,
            showBottomBar: false,
            view: {
                width: () => defaultSheetWidth,
                height: () => defaultSheetHeight,
            },
            col: {
                len: chartManager.AllChartSeries.length + 1 >= 7
                    ? chartManager.AllChartSeries.length + 1
                    : 7,
            },
        });

        spreadsheet.loadData([
            {
                rows: chartManager.chartTableData,
            },
        ]);

        spreadsheet.on('cell-edited', () => {
            chartManager.updateIsEdited(true);
        });

        spreadsheet.on('cell-blur', () => {
            if (chartManager.isEdited) {
                chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
                chartManager.updateIsEdited(false);
            }
        });

        spreadsheet.on('copy-paste', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        // 系统剪贴板粘贴事件
        spreadsheet.on('pasted-clipboard', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        spreadsheet.on('cut-paste', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        spreadsheet.on('insert-row', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        spreadsheet.on('delete-row', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        spreadsheet.on('insert-column', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        spreadsheet.on('delete-column', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        spreadsheet.on('delete-cell-text', () => {
            chartManager.convertAndSendTableDataToSdk(this.spreadsheetRef.getData(), spreadsheet);
        });

        this.spreadsheetRef = spreadsheet;
        chartManager.updateSpreadsheetInstance(spreadsheet);

        const modal = document.querySelector('.slide-spreadsheet-sheet');
        const modalDragger = document.querySelector('.slide-spread-sheet-dragger');

        if (modal && modalDragger) {
            modalDragger.addEventListener('dblclick', this.handlePreventDefaultAndPropagation);
            modalDragger.addEventListener('contextmenu', this.handlePreventDefaultAndPropagation);
            modalDragger.addEventListener('wheel', this.handlePreventPropagation);
            modalDragger.addEventListener('mouseenter', this.handlePreventTwoFingersSwipe);
            modalDragger.addEventListener('mouseleave', this.handleOpenTwoFingersSwipe);

            modal.addEventListener('mousedown', this.handlePreventDefaultAndPropagation);
            modal.addEventListener('click', this.handlePreventDefaultAndPropagation);
        }
    }

    public componentWillUnmount(): void {
        const modal = document.querySelector('.slide-spreadsheet-sheet');
        const modalDragger = document.querySelector('.slide-spread-sheet-dragger');

        if (modal && modalDragger) {
            modalDragger.removeEventListener('dblclick', this.handlePreventDefaultAndPropagation);
            modalDragger.removeEventListener('contextmenu', this.handlePreventDefaultAndPropagation);
            modalDragger.removeEventListener('wheel', this.handlePreventPropagation);
            modalDragger.removeEventListener('mouseenter', this.handlePreventTwoFingersSwipe);
            modalDragger.removeEventListener('mouseleave', this.handleOpenTwoFingersSwipe);

            modal.removeEventListener('mousedown', this.handlePreventDefaultAndPropagation);
            modal.removeEventListener('click', this.handlePreventDefaultAndPropagation);
        }
    }

    public render(): React.ReactNode {
        return <div id="slide-chart-spreadsheet" style={{ pointerEvents: 'auto' }}></div>;
    }
}
